import { format, isBefore, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const TZ = 'America/Fortaleza';

export function parseDateOnly(d: string): Date {
  // YYYY-MM-DD interpretado como data local
  const parts = d.split('-').map(Number);
  const y = parts[0] ?? 1970;
  const m = parts[1] ?? 1;
  const day = parts[2] ?? 1;
  return new Date(y, m - 1, day);
}

export function fmtDateBR(d: string, pattern = "dd 'de' MMM"): string {
  return format(parseDateOnly(d), pattern, { locale: ptBR });
}

export function fmtWeekdayLong(d: string): string {
  return format(parseDateOnly(d), "EEEE, dd 'de' MMMM", { locale: ptBR });
}

export function fmtWeekday(d: string): string {
  return format(parseDateOnly(d), 'EEE', { locale: ptBR });
}

export function fmtMonth(d: Date): string {
  return format(d, "MMMM 'de' yyyy", { locale: ptBR });
}

export function fmtTime(t: string | null | undefined): string {
  if (!t) return '';
  return t.slice(0, 5);
}

export function todayISO(): string {
  return format(new Date(), 'yyyy-MM-dd');
}

export function toISODate(d: Date): string {
  return format(d, 'yyyy-MM-dd');
}

/** Todas as datas de `inicio` a `fim`, inclusive. Vazio se o intervalo for inválido. */
export function datesInRange(inicio: string, fim: string): string[] {
  if (!inicio || !fim || fim < inicio) return [];
  const out: string[] = [];
  const cursor = parseDateOnly(inicio);
  const last = parseDateOnly(fim);
  while (cursor.getTime() <= last.getTime()) {
    out.push(toISODate(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return out;
}

export function isOverdue(due_date: string, due_time: string | null | undefined): boolean {
  const d = parseDateOnly(due_date);
  if (due_time) {
    const parts = due_time.slice(0, 5).split(':').map(Number);
    const h = parts[0] ?? 0;
    const m = parts[1] ?? 0;
    d.setHours(h, m, 0, 0);
  } else {
    d.setHours(23, 59, 59);
  }
  return isBefore(d, new Date());
}

export function fmtCouvert(
  type: string,
  value: number | null | undefined,
  until: string | null | undefined,
): string {
  if (type === 'free') return 'Free';
  if (type === 'free_until') {
    const t = fmtTime(until ?? null);
    if (!t) return 'Free';
    const [h, m] = t.split(':');
    const base = `Free até ${m === '00' ? `${h}h` : t}`;
    return value != null ? `${base} · R$ ${value}` : base;
  }
  if (type === 'flat' && value != null) return `R$ ${value}`;
  return '—';
}

export function fmtCouvertParts(
  type: string,
  value: number | null | undefined,
  until: string | null | undefined,
): { label: string; value: string } {
  if (type === 'free') return { label: 'Free a', value: 'Noite toda' };
  if (type === 'free_until') {
    const t = fmtTime(until ?? null);
    if (!t) return { label: 'Free a', value: 'Noite toda' };
    const [h, m] = t.split(':');
    const hora = m === '00' ? `${h}h` : t;
    return { label: 'Free até', value: value != null ? `${hora} · R$ ${value}` : hora };
  }
  if (type === 'flat' && value != null) return { label: 'Couvert', value: `R$ ${value}` };
  return { label: 'Couvert', value: '—' };
}

export function slugify(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

/* ── Aliases legacy ──────────────────────────────────────────────── */
export function formatDateBR(date: string | Date, fmt = "dd 'de' MMM"): string {
  if (typeof date === 'string') return fmtDateBR(date, fmt);
  return format(date, fmt, { locale: ptBR });
}

export function formatWeekday(date: string | Date): string {
  if (typeof date === 'string') return fmtWeekday(date);
  return format(date, 'EEE', { locale: ptBR });
}

export function formatTime(date: string | Date): string {
  if (typeof date === 'string') return fmtTime(date);
  return format(date, 'HH:mm');
}

export { TZ, parseISO };
